// A lazy chunk's stylesheet is not load-bearing the way its JavaScript is.
// Vite's preload helper disagrees: it appends a <link rel="stylesheet"> for
// every CSS dependency of a dynamic import and rejects the import outright if
// one of those links errors, without ever running the module. In production
// that link errors for one reason — a tab running an index.html from an
// earlier deploy asks for a hashed CSS file the CDN no longer serves — and the
// result is wildly out of proportion: React re-throws out of Suspense and the
// whole tree lands on the ErrorBoundary screen because a stylesheet moved.
//
// `vite:preloadError` is Vite's documented way out: preventDefault() and the
// helper carries on to the module itself, which renders unstyled at worst.
// We only do that for the CSS case. A missing *JS* chunk really is a stale
// build, and swallowing it here would leave React.lazy resolving to undefined
// instead of throwing, so those keep throwing and reach the retry-then-reload
// handling in lazy-with-retry.ts.

export const isCssPreloadError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return /Unable to preload CSS/i.test(message);
};

if (typeof window !== "undefined") {
  window.addEventListener("vite:preloadError", (event) => {
    const { payload } = event as Event & { payload?: unknown };
    if (isCssPreloadError(payload)) {
      console.warn("Stylesheet for a lazy chunk failed to load; continuing without it.", payload);
      event.preventDefault();
    }
  });
}
